import { useState } from 'react';
import observeService, { ObserveSiteUsage } from '../../services/ScaleoAdminApiServices/ObserveService';
import { Station } from '../../types/station';

const triggerDownload = (fileName: string, content: string) => {
  const url = window.URL.createObjectURL(new Blob([content], { type: 'text/plain;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  window.URL.revokeObjectURL(url);
};

export const useObserveConfig = (refetchStations: () => void) => {
  const [busyStationId, setBusyStationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const readErrorMessage = (e: any) =>
    e?.response?.data?.error ?? e?.response?.data?.message ?? e?.message ?? 'Nieznany błąd';

  // Downloading is an optional step - the site is provisioned automatically and the files only
  // matter for the manual push fallback, so this never creates anything on its own.
  const downloadObserveConfig = async (station: Station) => {
    if (!station.id) {
      return;
    }

    setBusyStationId(station.id);
    setError(null);

    try {
      const { data } = await observeService.getConfigFiles(station.id);
      data.files.forEach((file) => triggerDownload(file.name, file.content));
    } catch (e) {
      setError(readErrorMessage(e));
    } finally {
      setBusyStationId(null);
    }
  };

  // One site serves every station of the system, so the caller shows this list before deleting.
  const loadObserveSiteUsage = async (station: Station): Promise<ObserveSiteUsage | null> => {
    if (!station.id) {
      return null;
    }

    setError(null);

    try {
      const { data } = await observeService.getSiteUsage(station.id);

      return data;
    } catch (e) {
      setError(readErrorMessage(e));

      return null;
    }
  };

  const deleteObserveSite = async (station: Station) => {
    if (!station.id) {
      return false;
    }

    setBusyStationId(station.id);
    setError(null);

    try {
      await observeService.deleteSite(station.id);
      refetchStations();

      return true;
    } catch (e) {
      setError(readErrorMessage(e));

      return false;
    } finally {
      setBusyStationId(null);
    }
  };

  const provisionObserveSite = async (station: Station) => {
    if (!station.id) {
      return false;
    }

    setBusyStationId(station.id);
    setError(null);

    try {
      await observeService.provisionSite(station.id);
      refetchStations();

      return true;
    } catch (e) {
      setError(readErrorMessage(e));

      return false;
    } finally {
      setBusyStationId(null);
    }
  };

  return {
    downloadObserveConfig,
    provisionObserveSite,
    loadObserveSiteUsage,
    deleteObserveSite,
    busyStationId,
    error,
    clearError: () => setError(null),
  };
};
